import React, { createContext, useContext } from "react"

import { cn } from "@coval/ui/cn"

import { readOnlyInteractionGuardProps } from "./readOnlyGuard"

const ReadOnlyRegionContext = createContext(false)

interface ReadOnlyRegionProviderProps {
  readOnly: boolean
  children: React.ReactNode
}

/**
 * Marks a subtree as read-only for every `ReadOnlyFieldset` inside it.
 *
 * Forms place `ReadOnlyFieldset` around their field groups and inside each accordion's content, so accordion triggers stay outside any disabled fieldset and remain expandable while the fields they reveal are disabled.
 */
export function ReadOnlyRegionProvider({
  readOnly,
  children,
}: ReadOnlyRegionProviderProps) {
  return (
    <ReadOnlyRegionContext.Provider value={readOnly}>
      {children}
    </ReadOnlyRegionContext.Provider>
  )
}

/**
 * Whether the caller sits inside a read-only `ReadOnlyRegionProvider`, for widgets a disabled fieldset does not reach, such as code editors.
 */
export function useIsInReadOnlyRegion(): boolean {
  return useContext(ReadOnlyRegionContext)
}

type ReadOnlyFieldsetProps = Omit<
  React.FieldsetHTMLAttributes<HTMLFieldSetElement>,
  "disabled"
>

/**
 * A borderless fieldset that is disabled, with `readOnlyInteractionGuardProps` applied, when it sits inside a read-only `ReadOnlyRegionProvider`.
 *
 * It sets no margin of its own, so a parent `space-y-*` utility spaces it like any sibling card.
 */
export function ReadOnlyFieldset({
  className,
  children,
  ...props
}: ReadOnlyFieldsetProps) {
  const readOnly = useIsInReadOnlyRegion()

  return (
    <fieldset
      {...props}
      disabled={readOnly}
      className={cn("min-w-0 border-0 p-0", className)}
      {...readOnlyInteractionGuardProps(readOnly)}
    >
      {children}
    </fieldset>
  )
}
